import { createServer, type Server } from "node:http";
import { errorHtml, successHtml } from "./callback-html";

const CALLBACK_PORT = 13337;

// The redirect_uri uses the "localhost" hostname, which resolves to 127.0.0.1
// (IPv4) on some hosts and ::1 (IPv6) on others. Bind both loopback families so
// the browser's redirect connects regardless of how it resolves localhost.
const LOOPBACK_HOSTS = ["127.0.0.1", "::1"] as const;

/**
 * Starts a temporary HTTP server on the loopback callback port, waits for the
 * OAuth redirect to `/callback` carrying the expected `state`, responds with a
 * close-tab page, and resolves with the authorization code. Rejects if the
 * callback contains an error, the port is unavailable on every loopback family,
 * or it times out after 5 minutes.
 */
export function startCallbackServer(expectedState: string): Promise<string> {
	return new Promise((resolve, reject) => {
		const servers: Server[] = [];
		let settled = false;
		let bindFailures = 0;

		const settle = (run: () => void): void => {
			if (settled) return;
			settled = true;
			clearTimeout(timeout);
			for (const server of servers) {
				server.close();
				server.closeAllConnections();
			}
			run();
		};

		const timeout = setTimeout(
			() =>
				settle(() =>
					reject(new Error("Timed out waiting for OAuth callback (5 min).")),
				),
			5 * 60 * 1000,
		);

		for (const host of LOOPBACK_HOSTS) {
			const server = createServer((req, res) => {
				const url = new URL(req.url ?? "/", "http://localhost");

				// Ignore anything that isn't our callback (favicon, probes, prefetch)
				// so a stray request can't consume or abort the pending login.
				if (url.pathname !== "/callback") {
					res.writeHead(404, { "Content-Type": "text/plain; charset=utf-8" });
					res.end("Not found");
					return;
				}

				// Bind the response to this login: a missing/mismatched state is a
				// forged or cross-flow request — reject the request, not the login.
				if (url.searchParams.get("state") !== expectedState) {
					res.writeHead(400, { "Content-Type": "text/plain; charset=utf-8" });
					res.end("Invalid OAuth state");
					return;
				}

				const code = url.searchParams.get("code");
				const error = url.searchParams.get("error");

				res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
				// Settle only after the page has flushed — otherwise tearing the
				// socket down can reset the connection before the browser renders it.
				res.end(code ? successHtml : errorHtml(error), () => {
					settle(() => {
						if (code) resolve(code);
						else
							reject(new Error(`OAuth error: ${error ?? "no code returned"}`));
					});
				});
			});

			server.on("error", (err: NodeJS.ErrnoException) => {
				// Tolerate a loopback family that isn't available (e.g. IPv6
				// disabled); only fail if every family failed to bind.
				bindFailures += 1;
				if (bindFailures === LOOPBACK_HOSTS.length) settle(() => reject(err));
			});

			server.listen(CALLBACK_PORT, host);
			servers.push(server);
		}
	});
}
